import { resolveMicrosoftSsoSettings } from "./microsoftSsoDefaults";

export type AppConfig = {
  apiBaseUrl: string;
  /** Sent as `x-site-code` on auth and API calls (must match web, e.g. cls-dev). */
  siteCode: string;
  /**
   * Core Word chat stream (`POST /api/v{2|3}/word/chat/`).
   * Trailing slash is required. conversation_id / new_chat are derived from this URL.
   */
  chatbotBaseUrl: string;
  /**
   * Direct LLM Word chat base (`/llm-backend/v2/word/chat`) for endpoints
   * not yet proxied via Core (suggestion, ai-search stream).
   * Mở chi tiết VBQPPL dùng Core `legal_search/by_query` (WORD_PLUGIN_API).
   */
  llmWordBaseUrl: string;
  authLoginPath: string;
  authMicrosoftLoginPath: string;
  authGoogleLoginPath: string;
  microsoftSso: {
    enabled: boolean;
    clientId: string;
    tenantId: string;
  };
  authCreateUserPath: string;
  authOtpRequestPath: string;
  authOtpVerifyRegisterPath: string;
  authOtpVerifyForgotPasswordPath: string;
  /** Trailing slash, e.g. `/auth/password-reset/`. */
  authPasswordResetPath: string;
  refreshTokenPath: string;
  conversationPath: string;
  /** Core chatbot skills list (`GET /api/v{2|3}/chatbot/skills/`). */
  skillsPath: string;
  quotaPath: string;
  addinEnv: string;
  maxContextChars: number;
  /** DCM CMS base URL for GetMetaDataFile (dev proxy: `/dcm-cms`). */
  dcmCmsBaseUrl: string;
  /** Header `x-api-key` for DCM CMS API. */
  dcmCmsApiKey: string;
};

const LLM_BACKEND_PREFIX = "/llm-backend";

const STAGING_API_BASE = "https://cls-api.cmcai.vn/api/v3";
const STAGING_LLM_WORD_CHAT = "https://cls-ai.cmcai.vn/llm-backend/v2/word/chat";
const DEV_API_BASE = "https://dev-clegal-api.cmcai.vn/api/v2";
const DEV_LLM_WORD_CHAT = "https://cls-ai-dev.cmcai.vn/llm-backend/v2/word/chat";
const DEFAULT_DEV_PROXY_API_BASE = "/api/v2";
const DEFAULT_DEV_PROXY_LLM_WORD_CHAT = "/llm-backend/v2/word/chat";

type BuildDeployTarget = "dev" | "staging" | "prod";

function buildDeployTarget(): BuildDeployTarget {
  const raw = (import.meta.env.VITE_DEPLOY_TARGET as string | undefined)?.trim().toLowerCase();
  if (raw === "dev") return "dev";
  if (raw === "prod") return "prod";
  if (raw === "staging") return "staging";
  return "staging";
}

const deployTarget = buildDeployTarget();

const apiBaseFallback = import.meta.env.DEV
  ? DEFAULT_DEV_PROXY_API_BASE
  : deployTarget === "dev"
    ? DEV_API_BASE
    : STAGING_API_BASE;

const llmWordBaseFallback = import.meta.env.DEV
  ? DEFAULT_DEV_PROXY_LLM_WORD_CHAT
  : deployTarget === "dev"
    ? DEV_LLM_WORD_CHAT
    : STAGING_LLM_WORD_CHAT;

/** Commercial clegal API dùng `commercial` (kể cả local/dev). */
const siteCodeFallback = "commercial";

const DEFAULT_DCM_CMS_PROXY = "/dcm-cms";

/** Luôn dùng proxy cùng origin (`/dcm-cms`) — dev: Vite, deploy: nginx. Tránh CORS/405 từ CMS. */
const dcmCmsBaseFallback = DEFAULT_DCM_CMS_PROXY;

const resolvedApiBaseUrl = normalizeApiV3BaseUrl(readEnv("VITE_API_BASE_URL", apiBaseFallback));
const chatbotBaseFallback = joinBaseAndPath(resolvedApiBaseUrl, "/word/chat/");

export const appConfig: AppConfig = {
  apiBaseUrl: resolvedApiBaseUrl,
  siteCode: readEnv("VITE_SITE_CODE", siteCodeFallback),
  chatbotBaseUrl: readEnv("VITE_CHATBOT_BASE_URL", chatbotBaseFallback),
  llmWordBaseUrl: ensureLlmBackendPrefix(readEnv("VITE_LLM_WORD_BASE_URL", llmWordBaseFallback)),
  authLoginPath: readEnv("VITE_AUTH_LOGIN_PATH", "/auth/login"),
  authMicrosoftLoginPath: readEnv("VITE_AUTH_MICROSOFT_LOGIN_PATH", "/sso/microsoft/login"),
  authGoogleLoginPath: readEnv("VITE_AUTH_GOOGLE_LOGIN_PATH", "/auth/google-login"),
  microsoftSso: resolveMicrosoftSsoSettings({
    deployTarget,
    devServer: Boolean(import.meta.env.DEV),
    enabled: rawEnv("VITE_MICROSOFT_SSO_ENABLED"),
    clientId: rawEnv("VITE_MICROSOFT_SSO_CLIENT_ID"),
    tenantId: rawEnv("VITE_MICROSOFT_SSO_TENANT_ID")
  }),
  authCreateUserPath: readEnv("VITE_AUTH_CREATE_USER_PATH", "/auth/create-user"),
  authOtpRequestPath: readEnv("VITE_AUTH_OTP_REQUEST_PATH", "/auth/otp/request"),
  authOtpVerifyRegisterPath: readEnv(
    "VITE_AUTH_OTP_VERIFY_REGISTER_PATH",
    "/auth/otp/verify/register"
  ),
  authOtpVerifyForgotPasswordPath: readEnv(
    "VITE_AUTH_OTP_VERIFY_FORGOT_PASSWORD_PATH",
    "/auth/otp/verify/forgot-password"
  ),
  authPasswordResetPath: readEnv("VITE_AUTH_PASSWORD_RESET_PATH", "/auth/password-reset/"),
  refreshTokenPath: readEnv("VITE_AUTH_REFRESH_PATH", "/refresh-token/"),
  conversationPath: readEnv("VITE_CHATBOT_CONVERSATION_PATH", "/chatbot/conversation"),
  skillsPath: readEnv("VITE_CHATBOT_SKILLS_PATH", "/chatbot/skills/"),
  quotaPath: readEnv("VITE_PACKAGE_QUOTA_PATH", "/package/request-count"),
  addinEnv: readEnv("VITE_WORD_ADDIN_ENV", "prod"),
  maxContextChars: readPositiveInt(readEnv("VITE_WORD_MAX_CONTEXT_CHARS", "20000"), 20000),
  dcmCmsBaseUrl: readEnv("VITE_DCM_CMS_BASE_URL", dcmCmsBaseFallback),
  dcmCmsApiKey: readEnv("VITE_DCM_CMS_API_KEY", "")
};

/** Strip `/llm-backend` from v3 API base URLs (v3 core API does not use that prefix). */
export function normalizeApiV3BaseUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return trimmed;

  if (/^https?:\/\//i.test(trimmed)) {
    const parsed = new URL(trimmed);
    parsed.pathname = stripLlmBackendPrefix(parsed.pathname);
    return parsed.toString();
  }

  return stripLlmBackendPrefix(trimmed.startsWith("/") ? trimmed : `/${trimmed}`);
}

/** Prefix URLs whose path contains `/v2/` with `/llm-backend` (idempotent). */
export function ensureLlmBackendPrefix(url: string): string {
  const trimmed = url.trim();
  if (!trimmed || trimmed.includes(LLM_BACKEND_PREFIX)) {
    return trimmed;
  }

  const hasV2Segment = /\/v2(?:\/|$)/.test(trimmed);
  if (!hasV2Segment) {
    return trimmed;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    const parsed = new URL(trimmed);
    parsed.pathname = `${LLM_BACKEND_PREFIX}${parsed.pathname}`;
    return parsed.toString();
  }

  const path = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  return `${LLM_BACKEND_PREFIX}${path}`;
}

export function buildUrl(baseUrl: string, path: string): string {
  const base = baseUrl.replace(/\/+$/, "");
  const suffix = path.startsWith("/") ? path : `/${path}`;
  const combined = `${base}${suffix}`;
  // Core API (`/api/v2` | `/api/v3`) — không gắn prefix `/llm-backend`.
  if (/\/api\/v[23](?:\/|$)/.test(combined)) {
    return normalizeApiV3BaseUrl(combined);
  }
  return ensureLlmBackendPrefix(combined);
}

function stripLlmBackendPrefix(pathname: string): string {
  return pathname.replace(new RegExp(`^${LLM_BACKEND_PREFIX}(?=/api/v[23](?:/|$))`), "");
}

function joinBaseAndPath(baseUrl: string, path: string): string {
  const base = baseUrl.replace(/\/+$/, "");
  const suffix = path.startsWith("/") ? path : `/${path}`;
  return `${base}${suffix}`;
}

function rawEnv(key: string): string | undefined {
  const value = import.meta.env[key];
  return typeof value === "string" ? value : undefined;
}

function readEnv(key: string, fallback: string): string {
  const value = rawEnv(key);
  return value && value.trim() ? value.trim() : fallback;
}

function readPositiveInt(value: string, fallback: number): number {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}
